import { animated } from '@react-spring/web';
import { ScaleContinuousNumeric } from 'd3-scale';
import React, { Fragment, useCallback, useMemo } from 'react';

import { useThemeDefinition } from '@tanium/react-theme-definition';

import { useLocalizedTickFormatter } from '../../hooks/useLocalizedTickFormatter';
import { useTickTransition } from '../../hooks/useTickTransition';
import { useTickValues } from '../../hooks/useTickValues';
import { isNumberCoercible } from '../../utils/isNumberCoercible';
import { NumberCoercible } from '../../utils/NumberCoercible';
import { StringCoercible } from '../../utils/StringCoercible';
import { ScaleWithValueType } from '../../utils/SupportedScale';
import { TickFormatter } from '../../utils/TickFormatter';
import { CreatePortalFn } from '../../utils/usePortal';
import { AxisOrientation } from './AxisOrientation';
import { AxisStroke, TickLabelText } from './themeDefinitions';
import { TickLabel, TickLabelProps } from './TickLabel';

export type TickLabelRenderFn<T extends NumberCoercible | StringCoercible> = (
  props: TickLabelProps & { tickValue: T },
) => React.ReactNode;

export const defaulTickLabelRenderer: TickLabelRenderFn<StringCoercible> = ({ tickValue, ...props }) => (
  <TickLabel {...props} />
);

interface AxisOwnProps<TTick> {
  /**
   * A function (analogous to ReactDOM.createPortal) that renders the given content in the
   * associated portal.
   */
  createPortal: CreatePortalFn;
  /**
   * The total height for the axis.
   */
  height?: number;
  /**
   * The orientation of the axis, based on its position relative to the plot
   * area.
   */
  orientation: AxisOrientation;
  /**
   * The top offset for the axis, relative to the portal's placement.
   */
  portalTop: number;
  /**
   * The top offset for the axis, relative to the portal's placement.
   */
  portalLeft: number;
  /**
   * The approximate number of ticks to show along the axis. Defaults to 10.
   *
   * If `ticks` is specified, `ticks` will determine the actual ticks rendered instead of this prop.
   * `tickCount` may still be used to inform tick formatting if a custom `tickFormatter` is not
   * specified.
   */
  tickCount?: number;
  /**
   * A format function to use to format tick labels. By default, the scale's built-in tick formatter
   * is used (if available), or a fallback formatter if the scale does not have its own tick
   * formatter.
   */
  tickFormatter?: TickFormatter<TTick>;
  /**
   * A custom render function to display the tick label. If not specified, a default renderer is used.
   */
  tickLabelRenderer?: TickLabelRenderFn<TTick> | undefined;
  /**
   * A d3 scale used for rendering the axis.
   */
  scale: ScaleWithValueType<TTick>;
  /**
   * The set of (pre-scaled) values to use as ticks along the axis.
   *
   * This value takes priority over `tickCount`.
   */
  ticks?: TTick[];
  /**
   * The starting position to render the axis; i.e., the start of the range.
   */
  start: number;
  /**
   * The ending position to render the axis; i.e., the end of the range.
   */
  end: number;
  /**
   * The total width for the axis.
   */
  width?: number;
  /**
   * Whether animations are enabled.
   */
  isAnimated: boolean;
}

export type AxisProps<T> = AxisOwnProps<T>;

/**
 * The length of each tick line in pixels, as rendered perpendicular to the axis.
 */
export const TICK_LINE_SIZE_PX = 8;

/**
 * The padding between the end of each tick line and its tick label in pixels.
 */
const TICK_LINE_PADDING_PX = 4;

const TICK_ADJUSTMENT_PX = 0;

const AxisInner = <T extends NumberCoercible | StringCoercible>({
  createPortal,
  orientation,
  scale,
  tickCount = 10,
  ticks: ticksProp,
  tickFormatter,
  tickLabelRenderer = defaulTickLabelRenderer,
  start,
  end,
  isAnimated = true,
  height,
  width,
  portalTop,
  portalLeft,
}: AxisProps<T>): JSX.Element => {
  const [content1, content2] = useThemeDefinition([AxisStroke, TickLabelText]);
  const defaultTickFormatter = useLocalizedTickFormatter();

  const isVerticalAxis = orientation === 'left' || orientation === 'right';
  const tickLineMultiplier = orientation === 'left' || orientation === 'top' ? -1 : 1;
  const tickLineEnd = TICK_LINE_SIZE_PX * tickLineMultiplier;

  const genericTickFormatter = useCallback(
    (tick: T) => {
      if (isNumberCoercible(tick)) {
        return defaultTickFormatter(tick);
      }
      return tick.toString();
    },
    [defaultTickFormatter],
  );

  const ticks = useTickValues({ scale, tickCount, ticks: ticksProp });

  const formatFn = useMemo(() => {
    return (tickFormatter ??
      ('tickFormat' in scale
        ? (scale as ScaleContinuousNumeric<never, never>).tickFormat(tickCount)
        : genericTickFormatter)) as TickFormatter<T>;
  }, [scale, tickCount, tickFormatter, genericTickFormatter]);

  const ticksWithLabels = useMemo(() => ticks.map((tick) => ({ tick, label: formatFn(tick) })), [formatFn, ticks]);

  const commonTextProps = useMemo(() => {
    const tickLabelPaddingOffset = tickLineEnd + TICK_LINE_PADDING_PX * tickLineMultiplier;

    return isVerticalAxis
      ? {
          left: portalLeft + tickLabelPaddingOffset,
          maxLineCount: 1 as TickLabelProps['maxLineCount'],
          width: width !== undefined ? width - Math.abs(tickLabelPaddingOffset) : undefined,
        }
      : {
          top: portalTop + tickLineEnd,
          maxLineCount: 'bandwidth' in scale && scale.bandwidth() > 45 ? 2 : (1 as TickLabelProps['maxLineCount']),
          height: height !== undefined ? height - tickLineEnd : undefined,
        };
  }, [height, isVerticalAxis, portalLeft, portalTop, scale, tickLineEnd, tickLineMultiplier, width]);

  const transition = useTickTransition({ ticks: ticksWithLabels, tickGetter: (t) => t.tick, scale, isAnimated });

  const tickLabels = useMemo(() => {
    return transition((transitionProps, { label, tick }, { key }) => {
      const { interpolatedValue, opacity, normalizedValue } = transitionProps;

      const finalInterpolatedValue = interpolatedValue.to((v) => v + TICK_ADJUSTMENT_PX);

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- left position is set for vertical axis
      const left = isVerticalAxis ? normalizedValue.to(() => commonTextProps.left!) : finalInterpolatedValue;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- top position is set for horizontal axis
      const top = isVerticalAxis ? finalInterpolatedValue : normalizedValue.to(() => commonTextProps.top!);

      const { left: leftProp, top: topProp, ...otherTextProps } = commonTextProps;

      const tickLabel = tickLabelRenderer({
        tickValue: tick,
        color: content2,
        fontSize: 12,
        orientation,
        ...otherTextProps,
        left,
        top,
        opacity,
        children: label,
      });

      return createPortal(<React.Fragment key={key as string}>{tickLabel}</React.Fragment>);
    });
  }, [commonTextProps, content2, createPortal, isVerticalAxis, orientation, tickLabelRenderer, transition]);

  const tickLines = useMemo(() => {
    return transition((transitionProps) => {
      const { interpolatedValue, opacity } = transitionProps;

      return (
        <animated.line
          x1={isVerticalAxis ? 0 : interpolatedValue}
          x2={isVerticalAxis ? tickLineEnd : interpolatedValue}
          y1={isVerticalAxis ? interpolatedValue : 0}
          y2={isVerticalAxis ? interpolatedValue : tickLineEnd}
          stroke={content1}
          opacity={opacity}
        />
      );
    });
  }, [content1, isVerticalAxis, tickLineEnd, transition]);

  const axisLine = (
    <line
      x1={isVerticalAxis ? 0 : start}
      x2={isVerticalAxis ? 0 : end}
      y1={isVerticalAxis ? start : 0}
      y2={isVerticalAxis ? end : 0}
      stroke={content1}
    />
  );

  return (
    <>
      {tickLabels}
      {tickLines}
      {axisLine}
    </>
  );
};

export const Axis = React.memo(AxisInner) as typeof AxisInner;
